import React from 'react';
import styled, { css } from 'styled-components';
import propTypes from 'prop-types';
import PlaceHolder from '../../../../../../UI/Editor/EditSpace/EditorPane/RenderContent/placeholder';
import { isSafari } from '../../../../../../../../../utility/browsers';
import breakpointsMinWidth from '../../../../../../../../../styling/breakpointsMinWidth';
import getWordSelectionDoubleClick from '../utils/getWordSelectionDoubleClick';
import { universityRestriction } from '../../../../../../../../../utility/universityRestrictions';

const scoreFocus = ({ isFocused }) => {
  if (isFocused) {
    return css`
      border: 1px solid rgba(133, 16, 223, 1);
      box-sizing: border-box;
      border-radius: 4px;
    `;
  }
  return null;
};

const Score = styled.div`
  user-select: ${(props) => !props.isSafari && 'text !important'};
  -webkit-user-select: ${(props) => props.isSafari && 'text !important'};

  grid-column: 3/-1;
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 4px;
  box-sizing: border-box;
  position: relative;
  height: 40px;
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 16px;
  color: #333d47;
  width: 100%;
  display: flex;
  align-items: center;
  pointer-events: auto;
  cursor: text;
  padding: 0 10px;

  ${(props) => scoreFocus(props)}
  @media ${breakpointsMinWidth.mobileMicroLg} {
    padding: 0 16px;
  }

  @media ${breakpointsMinWidth.tabletSm} {
    height: 36px;
    padding: 0 9px;
  }

  @media ${breakpointsMinWidth.desktopSm} {
    padding: 0 10px;
  }

  @media ${breakpointsMinWidth.desktopMd} {
    height: 40px;
    padding: 0 14px;
    font-size: 14px;
  }
`;

const CommonScore = styled.span`
  display: flex;
  align-items: center;
  height: 100%;
  width: 50%;
  position: relative;
  line-height: 14px;
`;

const Min = styled(CommonScore)`
  padding: 0 10px 0 0;
  border-right: ${(props) =>
    props.isFocused ? '1px solid #8510DF' : '1px solid #e5e5e5'};

  @media ${breakpointsMinWidth.mobileMicroLg} {
    padding: 0 16px 0 0;
  }

  @media ${breakpointsMinWidth.tabletSm} {
    padding: 0 9px 0 0;
  }

  @media ${breakpointsMinWidth.desktopSm} {
    padding: 0 10px 0 0;
  }

  @media ${breakpointsMinWidth.desktopMd} {
    padding: 0 14px 0 0;
  }
`;

const Max = styled(CommonScore)`
  padding: 0 0 0 10px;
  @media ${breakpointsMinWidth.mobileMicroLg} {
    padding: 0 0 0 16px;
  }

  @media ${breakpointsMinWidth.tabletSm} {
    padding: 0 0 0 9px;
  }

  @media ${breakpointsMinWidth.desktopSm} {
    padding: 0 0 0 10px;
  }

  @media ${breakpointsMinWidth.desktopMd} {
    padding: 0 0 0 14px;
  }
`;

const ChildrenRender = styled.span`
  white-space: nowrap;
  width: -webkit-fill-available;
  overflow: auto;
  scrollbar-width: none;
  width: 100%;
  text-align: left;
  line-height: 20px;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const PlaceHolder2 = styled(PlaceHolder)`
  ${(props) => {
    const { isFocused, minNodeText } = props;

    if (isFocused || minNodeText) {
      return css`
        left: -4px;
        @media ${breakpointsMinWidth.mobileMicroLg} {
          left: -4px;
        }

        @media ${breakpointsMinWidth.tabletSm} {
          left: -4px;
        }

        @media ${breakpointsMinWidth.desktopSm} {
          left: -4px;
        }

        @media ${breakpointsMinWidth.desktopMd} {
          left: -4px;
        }
      `;
    }
    return null;
  }}
`;

const RoleScore = ({ nodeProps, universityPlan }) => {
  const { node, attributes, children, isFocused, editor } = nodeProps;
  const { nodes } = node;
  const nodesArray = nodes.toArray();
  const minNode = nodesArray[0];
  const maxNode = nodesArray[1];
  const {
    value: { focusBlock },
  } = editor;

  return (
    <Score
      {...attributes}
      nodeType={node.type}
      isFocused={isFocused}
      isSafari={isSafari}
      ariaLabel={node.type}
    >
      <Min isFocused={isFocused}>
        <PlaceHolder2
          contentEditable={false}
          minNode={minNode}
          minNodeText={minNode.text}
          isFocused={isFocused}
        >
          {universityRestriction[universityPlan?.code]?.editor?.resume
            ?.education?.score || 'Score'}
        </PlaceHolder2>
        <ChildrenRender
          text={minNode.text}
          aria-label="Min Score"
          data-testid={`${node.type}-${minNode.key}`}
          onMouseDown={(e) => {
            if (e.detail === 2) {
              getWordSelectionDoubleClick({ editor });
            }
            if (e.detail >= 3) {
              e.preventDefault();
              e.stopPropagation();
              editor.moveToRangeOfNode(focusBlock);
            }
          }}
        >
          {children[0]}
        </ChildrenRender>
      </Min>
      {/* <Separator contentEditable={false}> / </Separator> */}
      <Max>
        <PlaceHolder
          contentEditable={false}
          minNode={maxNode}
          minNodeText={maxNode.text}
          isFocused={isFocused}
        >
          {universityRestriction[universityPlan?.code]?.editor?.resume
            ?.education?.maxScore || 'Max Score'}
        </PlaceHolder>
        <ChildrenRender
          aria-label="Max Score"
          data-testid={`${node.type}-${maxNode.key}`}
          onMouseDown={(e) => {
            if (e.detail === 2) {
              getWordSelectionDoubleClick({ editor });
            }
            if (e.detail >= 3) {
              e.preventDefault();
              e.stopPropagation();
              editor.moveToRangeOfNode(focusBlock);
            }
          }}
          text={maxNode && maxNode.text}
        >
          {children[1]}
        </ChildrenRender>
      </Max>
    </Score>
  );
};

RoleScore.propTypes = {
  nodeProps: propTypes.objectOf(propTypes.object).isRequired,
};

export default RoleScore;
